// @copyright  2006-2026 West Indies Software Engineering Company Limited. All rights reserved.

/*
 * There are deliberately no `stale` keys here. `frontend-11` struck them on
 * 2026-08-09: no endpoint this screen calls returns a staleness flag, so copy
 * for that state would be fed by a field that never arrives.
 *
 * THE RETURN'S NAME IS NOT DECORATED. `return_type` is
 * `tax_returns.tax_return_name` copied onto the approval row — already a
 * written name ("S04 Form", "GCT Return"), not a code — so no key here wraps it
 * in "{type} return". The specification's example did, and would have produced
 * "GCT Return return" against the real data.
 *
 * "You do not have access to this portal." is NOT repeated here. It is one fact
 * about the portal rather than about approvals, the task detail screen already
 * reuses the task list's wording for it, and a second copy of a sentence is a
 * sentence that eventually drifts from the first.
 */

export default {
    '$dir': 'ltr',

    // `…approvals.title` was deleted on 2026-08-13 (user ruling: no page title
    // on any client portal action bar).
    'portals.clients.approvals.empty': 'Nothing needs your approval right now.',
    'portals.clients.approvals.period': '{from} to {to}',
    'portals.clients.approvals.approve': 'Approve',
    'portals.clients.approvals.reject': 'Reject',
    'portals.clients.approvals.confirmTitle': 'Approve this return?',
    'portals.clients.approvals.confirmBody': 'By approving, you are confirming these figures are correct and authorising {practice} to submit this return on your behalf.',
    'portals.clients.approvals.confirmFinal': 'You cannot change this afterwards.',
    'portals.clients.approvals.rejectReason': 'Tell them what is wrong',
    'portals.clients.approvals.rejectRequired': 'Please say what is wrong so they can put it right.',
    // AMENDED 2026-08-14 (several portal contacts per client). These two read
    // "You approved this on {date}." and "You rejected this on {date}." until a
    // client could have more than one person in the portal — at which point
    // "you" is a lie roughly half the time, and a lie about who authorised a
    // filing is the worst kind on this screen. They are now actor-free and are
    // the fallback for a decision whose decider cannot be named at all; the
    // named forms below are what the list normally shows.
    'portals.clients.approvals.decidedApproved': 'Approved on {date}.',
    'portals.clients.approvals.decidedRejected': 'Rejected on {date}.',

    // Added by this screen. The catalogue above is the specified set; these cover
    // what the screen actually has to put into words — the three sets it can
    // show, the figures it names, every state a load or a decision can land in,
    // and the two dialogues.
    'portals.clients.approvals.sentBy': 'Sent by {practice}',
    'portals.clients.approvals.loading': 'Loading the returns waiting for you.',
    'portals.clients.approvals.error': 'We could not load your returns. Try again.',
    'portals.clients.approvals.tryAgain': 'Try again',
    'portals.clients.approvals.showMore': 'Show more',
    'portals.clients.approvals.readOnly': 'This work has ended. You can still see what was asked.',

    // The three sets, and what each looks like when it is empty. "Awaiting you"
    // rather than "Pending", because a client says the first and an accountant
    // says the second.
    'portals.clients.approvals.filterLegend': 'Which returns to show',
    'portals.clients.approvals.filterPending': 'Awaiting you',
    'portals.clients.approvals.filterApproved': 'Approved',
    'portals.clients.approvals.filterRejected': 'Rejected',
    'portals.clients.approvals.emptyHeading': 'You are all up to date',
    'portals.clients.approvals.emptyApprovedHeading': 'Nothing approved yet',
    'portals.clients.approvals.emptyApproved': 'Returns you approve will be listed here.',
    'portals.clients.approvals.emptyRejectedHeading': 'Nothing rejected',
    'portals.clients.approvals.emptyRejected': 'Returns you send back will be listed here.',

    // What one return says on the card. The labels are written out rather than
    // left to position, because the amount is the figure being signed for.
    'portals.clients.approvals.periodLabel': 'Period',
    'portals.clients.approvals.amountLabel': 'Amount',
    'portals.clients.approvals.requestedOn': 'Sent to you {date}',
    'portals.clients.approvals.awaitingMarker': 'Awaiting your decision',
    'portals.clients.approvals.approvedMarker': 'Approved',
    'portals.clients.approvals.rejectedMarker': 'Rejected',
    'portals.clients.approvals.decidedApprovedNoDate': 'Approved.',
    'portals.clients.approvals.decidedRejectedNoDate': 'Rejected.',
    'portals.clients.approvals.reasonGiven': 'Reason given: {reason}',

    // WHO DECIDED IT (`FR-07`, 2026-08-14). A client may now have several people
    // in the portal, so a decided return names the one who signed it — the
    // question "who approved this?" is exactly what somebody comes back to this
    // list months later to answer, and it is unanswerable from a date alone.
    //
    // Four forms rather than one, mirroring the four already here: the decision
    // and the presence of a timestamp are independent, and a single string with
    // an optional clause produces "Approved by Delroy Marchmont on ." the first
    // time a date is missing.
    'portals.clients.approvals.decidedApprovedBy': 'Approved by {name} on {date}.',
    'portals.clients.approvals.decidedRejectedBy': 'Rejected by {name} on {date}.',
    'portals.clients.approvals.decidedApprovedByNoDate': 'Approved by {name}.',
    'portals.clients.approvals.decidedRejectedByNoDate': 'Rejected by {name}.',
    'portals.clients.approvals.reasonGivenBy': '{name} told them: {reason}',

    // The two dialogues. `approveNamed` and `rejectNamed` are the buttons' spoken
    // labels: with several returns on screen, "Approve" alone tells a screen
    // reader nothing about WHICH return is about to be authorised.
    'portals.clients.approvals.approveNamed': 'Approve the {return} for {period}',
    'portals.clients.approvals.rejectNamed': 'Reject the {return} for {period}',
    'portals.clients.approvals.rejectTitle': 'Send this return back?',
    'portals.clients.approvals.rejectBody': '{practice} will not file this return. They will put it right and send it to you again.',
    'portals.clients.approvals.rejectHelp': 'One sentence is enough. Up to 500 characters.',
    'portals.clients.approvals.confirmApprove': 'Approve and authorise filing',
    'portals.clients.approvals.confirmReject': 'Send it back',
    'portals.clients.approvals.cancel': 'Cancel',
    'portals.clients.approvals.deciding': 'Sending your decision…',
    'portals.clients.approvals.decisionFailed': 'That did not work. Try again.',
    'portals.clients.approvals.approvedJustNow': 'Thank you. {practice} can now file this return.',
    'portals.clients.approvals.rejectedJustNow': 'Sent back to {practice} with your reason.',
};
